/**
 * Media Form Handler Module
 * Handles image picker UI interactions and file selection.
 */

import { Roles } from "../constants/roles";
import { Actions } from "../constants/actions";
import { FormBase } from "../util/form-base-form";
import { WysiwygRender } from "./wysiwyg-render";

type SingleFileHandler = (file: File) => Promise<void> | void;
type MultiFileHandler = (files: FileList) => Promise<void> | void;

class WysiwygFormBase extends FormBase {
    public override fromEventTarget<T extends Element = HTMLElement>(
        root: Event | EventTarget | Element | null | undefined,
        elementClass?: abstract new (...args: never[]) => T,
    ): T | null {
        return super.fromEventTarget(root, elementClass);
    }

    public getClosestElement<T extends Element = HTMLElement>(
        root: Event | EventTarget | Element | null | undefined,
        selector: string,
        elementClass?: abstract new (...args: never[]) => T,
    ): T | null {
        return this.closest(root, selector, elementClass);
    }

    public override queryElement<T extends Element = HTMLElement>(
        root: ParentNode | null | undefined,
        selector: string,
        elementClass?: abstract new (...args: never[]) => T,
    ): T | null {
        return super.queryElement(root, selector, elementClass);
    }

    public override queryElements<T extends Element = HTMLElement>(
        root: ParentNode | null | undefined,
        selector: string,
        elementClass?: abstract new (...args: never[]) => T,
    ): T[] {
        return super.queryElements(root, selector, elementClass);
    }

    public getForm(root: Event | EventTarget | Element | null | undefined): HTMLFormElement | null {
        return super.getForm(root);
    }
}

const formBase = new WysiwygFormBase();

/**
 * WysiwygForm class
 * Manages file input dialogs for image and video selection.
 */
class WysiwygForm {
    public static getTransientFileInput(role: string): HTMLInputElement | null {
        return formBase.queryElement(document.body, `[data-role="${role}"]`, HTMLInputElement);
    }

    public static getEventTarget(event: Event): Element | null {
        return formBase.fromEventTarget(event, Element);
    }

    public static getContentImageDeleteButton(event: Event): HTMLElement | null {
        return formBase.getClosestElement(
            WysiwygForm.getEventTarget(event),
            ".btn-delete-content-image[data-filename]",
            HTMLElement,
        );
    }

    public static getMentionUserOption(event: Event): HTMLElement | null {
        return formBase.getClosestElement(
            WysiwygForm.getEventTarget(event),
            '[data-action="select-user"][data-user-id]',
            HTMLElement,
        );
    }

    public static getImageToolbarButton(event: Event): HTMLButtonElement | null {
        return formBase.getClosestElement(WysiwygForm.getEventTarget(event), "button[data-action]", HTMLButtonElement);
    }

    public static getImageToolbarSizeSlider(root: ParentNode): HTMLInputElement | null {
        return formBase.queryElement(root, `input[data-action="${Actions.SET_IMAGE_SIZE}"]`, HTMLInputElement);
    }

    public static getImageToolbarSizeLabel(root: ParentNode): HTMLElement | null {
        return formBase.queryElement(root, ".wysiwyg-image-toolbar__size-label", HTMLElement);
    }

    public static getMentionUserOptionById(root: ParentNode, optionId: string): HTMLElement | null {
        return formBase.queryElement(root, `#${CSS.escape(optionId)}`, HTMLElement);
    }

    public static getSnsLinksList(root: ParentNode): HTMLElement | null {
        return formBase.queryElement(root, `[data-role="${Roles.SNS_LINKS_LIST}"]`, HTMLElement);
    }

    public static getSnsLinkInputs(root: ParentNode): HTMLInputElement[] {
        return formBase.queryElements(root, `[data-role="${Roles.SNS_LINK_INPUT}"]`, HTMLInputElement);
    }

    public static getHiddenSnsLinkInputs(root: ParentNode): HTMLInputElement[] {
        return formBase.queryElements(root, 'input[type="hidden"][name="sns_links[]"]', HTMLInputElement);
    }

    public static removeHiddenSnsLinkInputs(root: ParentNode): void {
        WysiwygForm.getHiddenSnsLinkInputs(root).forEach((element) => element.remove());
    }

    public static getUploadPlaceholder(root: ParentNode, uploadId: string): HTMLElement | null {
        return formBase.queryElement(root, `[data-upload-id="${uploadId}"]`, HTMLElement);
    }

    public static getAllDescendantElements(root: ParentNode): HTMLElement[] {
        return formBase.queryElements(root, "*", HTMLElement);
    }

    public static getContentImagesList(boardId: string, root: ParentNode = document): HTMLElement | null {
        return formBase.queryElement(
            root,
            `[data-role="${Roles.CONTENT_IMAGES_LIST}"][data-board-id="${boardId}"]`,
            HTMLElement,
        );
    }

    public static getImages(root: ParentNode): HTMLImageElement[] {
        return formBase.queryElements(root, "img", HTMLImageElement);
    }

    public static getMountedEditor(root: ParentNode, mountId: string): HTMLElement | null {
        return formBase.queryElement(root, `[data-wysiwyg-mount-id="${mountId}"]`, HTMLElement);
    }

    public static getOgpPreviewContainerForTextarea(textarea: HTMLTextAreaElement): HTMLElement | null {
        return (
            formBase.queryElement(textarea.form, `[data-role="${Roles.OGP_LIVE_PREVIEW}"]`, HTMLElement) ||
            formBase.queryElement(
                formBase.getClosestElement(textarea, `[data-role="${Roles.POST_CREATE_FORM}"]`, HTMLElement),
                `[data-role="${Roles.OGP_LIVE_PREVIEW}"]`,
                HTMLElement,
            ) ||
            formBase.queryElement(document, `[data-role="${Roles.OGP_LIVE_PREVIEW}"]`, HTMLElement)
        );
    }

    public static getOgpPreviewTabContainerForTextarea(textarea: HTMLTextAreaElement): HTMLElement | null {
        return (
            formBase.queryElement(textarea.form, `[data-role="${Roles.OGP_PREVIEW_TAB}"]`, HTMLElement) ||
            formBase.queryElement(
                formBase.getClosestElement(textarea, `[data-role="${Roles.POST_CREATE_FORM}"]`, HTMLElement),
                `[data-role="${Roles.OGP_PREVIEW_TAB}"]`,
                HTMLElement,
            ) ||
            formBase.queryElement(document, `[data-role="${Roles.OGP_PREVIEW_TAB}"]`, HTMLElement)
        );
    }

    public static getImagesByFilename(root: ParentNode, filename: string): HTMLImageElement[] {
        return formBase.queryElements(root, `img[src*="${CSS.escape(filename)}"]`, HTMLImageElement);
    }

    public static getToolbarHeaderSelect(root: ParentNode): HTMLSelectElement | null {
        return formBase.queryElement(root, "select.ql-header", HTMLSelectElement);
    }

    public static getInstagramAnchor(root: ParentNode): HTMLAnchorElement | null {
        return formBase.queryElement(root, 'a[href*="instagram.com"]', HTMLAnchorElement);
    }

    public static getFormContentImagesList(textarea: HTMLTextAreaElement): HTMLElement | null {
        const boardId = String(textarea?.dataset?.boardId || "default").trim();
        return WysiwygForm.getContentImagesList(boardId, textarea.form || document);
    }

    /**
     * The WYSIWYG "insert product" toolbar button reuses the same search
     * input/modal as the single "Target Product" linker (post-product-linker)
     * rather than a second parallel instance — see
     * wysiwyg/wysiwyg-product-embed/ and ProductLinkerHandler.handleProductSelected
     * (guarded by the same input's data-wysiwyg-embed-pending flag).
     */
    public static getFormProductEmbedSearchInput(textarea: HTMLTextAreaElement): HTMLInputElement | null {
        return (
            formBase.queryElement(
                textarea.form,
                `[data-role="${Roles.POST_PRODUCT_SEARCH_INPUT}"]`,
                HTMLInputElement,
            ) ||
            formBase.queryElement(
                formBase.getClosestElement(textarea, `[data-role="${Roles.POST_CREATE_FORM}"]`, HTMLElement),
                `[data-role="${Roles.POST_PRODUCT_SEARCH_INPUT}"]`,
                HTMLInputElement,
            ) ||
            formBase.queryElement(document, `[data-role="${Roles.POST_PRODUCT_SEARCH_INPUT}"]`, HTMLInputElement)
        );
    }

    public static openImagePicker(onFileSelected?: SingleFileHandler | null): void {
        void (async () => {
            const files = await WysiwygRender.openTransientFilePicker({ accept: "image/*", multiple: false });
            const file = files && files.length ? files[0] : null;
            if (file && onFileSelected) {
                await onFileSelected(file);
            }
        })();
    }

    public static openVideoPicker(onFileSelected?: SingleFileHandler | null): void {
        void (async () => {
            const files = await WysiwygRender.openTransientFilePicker({ accept: "video/*", multiple: false });
            const file = files && files.length ? files[0] : null;
            if (file && onFileSelected) {
                await onFileSelected(file);
            }
        })();
    }

    public static openImagePickerMultiple(onFilesSelected?: MultiFileHandler | null): void {
        void (async () => {
            const files = await WysiwygRender.openTransientFilePicker({ accept: "image/*", multiple: true });
            if (files && files.length && onFilesSelected) {
                await onFilesSelected(files);
            }
        })();
    }

    public static openVideoPickerMultiple(onFilesSelected?: MultiFileHandler | null): void {
        void (async () => {
            const files = await WysiwygRender.openTransientFilePicker({ accept: "video/*", multiple: true });
            if (files && files.length && onFilesSelected) {
                await onFilesSelected(files);
            }
        })();
    }
}

export { WysiwygForm };
